import { t } from '@lingui/core/macro';
import { z } from 'zod';

import { encodeFilterFieldName } from '@/components/filters/fieldName';
import { FilterField } from '@/components/filters/FilterField';
import { useFilterForm } from '@/components/filters/useFilterForm';
import { Field, FieldError } from '@/components/Form/fields';
import type { FilterOperatorRange } from '@/components/InstantSearch/types';
import type { FilterComponentProps } from '@/providers/TableState';

import RangeControl from './RangeControl';

type Props = FilterComponentProps & {
  type?: 'number' | 'percent';
};

const RangeFilter = ({ name, label, infoPopover, compact, ...rest }: Props) => {
  const form = useFilterForm();

  return (
    <form.AppField
      name={encodeFilterFieldName(name)}
      validators={{
        onChange: z.nullish(
          z
            .object({
              min: z.optional(
                z.object({
                  inclusive: z.boolean(),
                  operand: z.union([z.string(), z.number(), z.null(), z.undefined()]),
                }),
              ),
              max: z.optional(
                z.object({
                  inclusive: z.boolean(),
                  operand: z.union([z.string(), z.number(), z.null(), z.undefined()]),
                }),
              ),
            })
            .refine(
              (range) => {
                const min = range.min?.operand;
                const max = range.max?.operand;
                return min == null || max == null || +min <= +max;
              },
              t`Min value must be less than the max`,
            ),
        ),
      }}
    >
      {(field) => {
        const rangeValue = field.state.value as FilterOperatorRange | null | undefined;
        const errors = field.state.meta.errors;
        const hasError = errors.length > 0;

        return (
          <Field data-invalid={hasError}>
            <FilterField
              data-testid={`${name}-range-filter`}
              label={label}
              infoPopover={infoPopover}
              hideLabel={!compact}
            >
              <RangeControl
                compact={compact}
                autoFocus={!compact}
                value={rangeValue}
                onChange={field.handleChange}
                status={hasError ? 'error' : undefined}
                {...rest}
              />
              {hasError && <FieldError className="mt-2" errors={errors} />}
            </FilterField>
          </Field>
        );
      }}
    </form.AppField>
  );
};

export default RangeFilter;
